import { isAxiosError } from 'axios';

import { API_DOMAIN, NFC_URL } from 'config/constants';
import { api } from 'lib/api';
import { checkStatus } from 'lib/helpers';
import { zWalletOperationPagination } from 'lib/models/walletOperations';

import { z } from 'zod';

/**
 * The NFC cards the bar charges against, as the admin-only `/nfc-card` routes answer them.
 *
 * Every card also has a shadow user that holds its balance. Its username is part of the
 * API's answer but never of this record: the schema below strips it, so the panel cannot
 * show it or send it anywhere.
 */

export const zNfcCardState = z.enum(['active', 'blocked', 'expired']);

export type NfcCardState = z.infer<typeof zNfcCardState>;

export const nfcCardStates = zNfcCardState.options;

/**
 * A card. `balance` and `operations` (balance movements plus product loads) are only
 * answered by the listing, which works them out for every card of the page; a card read on
 * its own does not carry them.
 *
 * The API never answers a card's UID, nor what it stores of it (MUTE-606): a card is named
 * by its id, in the routes of the API and in the panel's own. `username` is its shadow
 * user's, opaque, only used to tell that user apart from the people in its movements.
 */
export const zNfcCard = z.object({
	_id: z.string(),
	username: z.string().nullish(),
	state: zNfcCardState,
	label: z.string().nullish(),
	responsable: z.string().nullish(),
	expiresAt: z.string().nullish(),
	createdAt: z.string().nullish(),
	balance: z.number().nullish(),
	operations: z.number().nullish(),
});

const zNfcCardsPage = z.object({
	total: z.number(),
	elements: z.array(zNfcCard),
});

const NFC_UID_MIN_LENGTH = 8;
const NFC_UID_MAX_LENGTH = 32;

/**
 * Whether a value is a card id, which is what the panel's URLs carry. Anything else, such as
 * a UID typed into the address bar, is not looked up.
 *
 * @param value A path or query parameter
 */
export function isNfcCardId(value: string): boolean {
	return /^[0-9a-f]{24}$/i.test(value);
}

/** The longest label the API accepts. */
export const NFC_CARD_LABEL_MAX_LENGTH = 100;

/**
 * A UID in the shape the API reads it, or null when it is not one.
 *
 * Mirrors `normalizeNfcUid` in the API: spaces, colons and dashes are dropped, the rest is
 * upper cased and must be 8 to 32 hexadecimal characters. The API is still the one that
 * decides; this only keeps the save button off for a UID it would refuse.
 *
 * @param raw What was typed or read
 */
export function normalizeNfcUid(raw: string): string | null {
	const uid = raw.replace(/[\s:-]/g, '').toUpperCase();

	if (uid.length < NFC_UID_MIN_LENGTH || uid.length > NFC_UID_MAX_LENGTH || !/^[0-9A-F]+$/.test(uid)) {
		return null;
	}

	return uid;
}

/** What the listing can be sorted by, besides its default of newest first. */
export const nfcCardSortFields = ['balance', 'operations', 'expiresAt', 'responsable'] as const;

export type NfcCardSortField = (typeof nfcCardSortFields)[number];

export type NfcCardSort = { field: NfcCardSortField; direction: 'asc' | 'desc' };

export type NfcCardSearch = {
	/** One card by its id: the card just read. */
	_id?: string | null;
	state?: NfcCardState | null;
	label?: string | null;
	responsable?: string | null;
	/** Newest first when there is none. */
	sort?: NfcCardSort | null;
	page: number;
	rowsPerPage: number;
};

export type NfcCardsPage = PaginateSource<NfcCard> & {
	/** Whether the listing could not be read, which is not the same as an empty one. */
	failed: boolean;
};

/** The largest page of the listing the API answers. */
export const NFC_CARDS_MAX_LIMIT = 200;

/**
 * One page of the cards, each with its balance and its number of operations, narrowed by
 * id, state, label and responsable, and sorted and paged by the API.
 *
 * The API only takes those four in its filter and refuses anything else, so the filter is
 * built here rather than through `getSearch`. The label and the responsable match by any
 * part of them, without telling upper from lower case.
 *
 * @param search What to narrow to, how to sort, and which page is wanted
 */
export async function fetchNfcCards(search: NfcCardSearch): Promise<NfcCardsPage> {
	try {
		const filter = {
			...(search._id ? { _id: search._id } : {}),
			...(search.state ? { state: search.state } : {}),
			...(search.label ? { label: search.label } : {}),
			...(search.responsable ? { responsable: search.responsable } : {}),
		};

		const limit = Math.min(search.rowsPerPage, NFC_CARDS_MAX_LIMIT);

		const response = await api.get(`${API_DOMAIN}${NFC_URL}`, {
			params: {
				filter: JSON.stringify(filter),
				options: JSON.stringify({
					limit,
					skip: search.page * limit,
					...(search.sort ? { sort: { [search.sort.field]: search.sort.direction === 'asc' ? 1 : -1 } } : {}),
				}),
			},
		});

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return { ...zNfcCardsPage.parse(response.data), failed: false };
	} catch (error) {
		console.error(error);
		return { elements: [], total: 0, failed: true };
	}
}

/**
 * One card by its opaque API id.
 *
 * @param cardId The card id
 */
export async function fetchNfcCard(cardId: string): Promise<NfcCard | null> {
	// An old link with a UID in it is not sent to the API, where it would end up in the logs
	if (!isNfcCardId(cardId)) return null;

	try {
		const response = await api.get(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return zNfcCard.parse(response.data);
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * The card a UID read with Web NFC belongs to, or null when no card has it.
 *
 * The UID goes in the body of the request, never in a URL, where it would stay in the
 * browser history and in the server and load balancer logs (MUTE-606). It is not kept: the
 * panel goes on with the card's id.
 *
 * @param uid The UID as the reader handed it over
 *
 * @throws The request error when the lookup fails for another reason than a missing card
 */
export async function lookupNfcCard(uid: string): Promise<NfcCard | null> {
	try {
		const response = await api.post(`${API_DOMAIN}${NFC_URL}/lookup`, { uid });

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return zNfcCard.parse(response.data);
	} catch (error) {
		if (isAxiosError(error) && error.response?.status === 404) return null;
		throw error;
	}
}

/**
 * The labels or the responsables the cards have, sorted, for the filters of the listing and
 * the suggestions of a new batch. Empty when they could not be read: the screens still work,
 * with nothing to pick from.
 *
 * @param field Which of the two
 */
export async function fetchNfcCardValues(field: 'labels' | 'responsables'): Promise<string[]> {
	try {
		const response = await api.get(`${API_DOMAIN}${NFC_URL}/${field}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return z.array(z.string()).parse(response.data);
	} catch (error) {
		console.error(error);
		return [];
	}
}

/**
 * How many cards of a label blocking (or unblocking) all of them would change: the ones that
 * are not already in that state. The label matches exactly.
 *
 * @param label The label
 * @param blocked Whether the cards are to be blocked or unblocked
 *
 * @throws The request error when the API refuses it
 */
export async function countLabelStateChange(label: string, blocked: boolean): Promise<number> {
	const response = await api.get(`${API_DOMAIN}${NFC_URL}/${blocked ? 'block' : 'unblock'}-by-label`, {
		params: { label },
	});

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return z.object({ count: z.number() }).parse(response.data).count;
}

/**
 * Blocks or unblocks every card of a label, matched exactly.
 *
 * @param label The label
 * @param blocked Whether the cards are to be blocked or unblocked
 *
 * @returns How many cards it changed
 *
 * @throws The request error when the API refuses it
 */
export async function setLabelBlocked(label: string, blocked: boolean): Promise<number> {
	const response = await api.post(`${API_DOMAIN}${NFC_URL}/${blocked ? 'block' : 'unblock'}-by-label`, { label });

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return z.object({ changed: z.number() }).parse(response.data).changed;
}

/**
 * Blocks a card, or moves it back to active.
 *
 * Unblocking an expired card is refused by the API with a 409, which is thrown as is.
 *
 * @param cardId The card id
 * @param state The state it should end up in
 */
export async function updateNfcCardState(cardId: string, state: 'active' | 'blocked'): Promise<NfcCard> {
	const action = state === 'blocked' ? 'block' : 'unblock';

	const response = await api.post(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/${action}`);

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return zNfcCard.parse(response.data);
}

/**
 * Gives a card a new expiration, or none, which is how an expired card is used again: an
 * unblock does not undo an expiration. The API refuses a date that already went by, and
 * leaves a blocked card blocked (MUTE-622).
 *
 * @param cardId The card id
 * @param expiresAt The new expiration as an ISO date, or null for a card that never expires
 *
 * @throws The request error when the API refuses it
 */
export async function updateNfcCardExpiration(cardId: string, expiresAt: string | null): Promise<NfcCard> {
	const response = await api.post(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/expiration`, { expiresAt });

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return zNfcCard.parse(response.data);
}

/**
 * Whether the API refuses a card for being expired: marked so, or past its expiration date.
 * Mirrors `isExpired` in the API, so the panel says it before an action is refused.
 */
export function isNfcCardExpired(card: Pick<NfcCard, 'state' | 'expiresAt'>): boolean {
	return card.state === 'expired' || (!!card.expiresAt && new Date(card.expiresAt).getTime() <= Date.now());
}

/** The most cards the API loads in one batch. */
export const NFC_BATCH_MAX_CARDS = 200;

/**
 * The status of an order loaded on a card: `active` is waiting to be picked up at the bar,
 * `closed` was picked up and `cancelled` was undone. Split orders never reach the panel:
 * the API answers the two orders they were split into instead.
 */
export const zNfcCardOrderStatus = z.enum(['active', 'closed', 'cancelled']);

export type NfcCardOrderStatus = z.infer<typeof zNfcCardOrderStatus>;

export const zNfcCardOrderProduct = z.object({
	productId: z.string(),
	title: z.string(),
	price: z.number(),
	quantity: z.number(),
	mixers: z.array(z.object({ mixer: z.string(), quantity: z.number() })).nullish(),
});

export type NfcCardOrderProduct = z.infer<typeof zNfcCardOrderProduct>;

/**
 * An order loaded on a card. Like the card itself, it is read without the shadow user it
 * is addressed to: the schema strips `orderTo`.
 */
export const zNfcCardOrder = z.object({
	_id: z.string(),
	code: z.string().nullish(),
	section: z.string().nullish(),
	products: z.array(zNfcCardOrderProduct),
	total: z.number(),
	status: zNfcCardOrderStatus,
	createdAt: z.string().nullish(),
	confirmationDate: z.string().nullish(),
	cancellationDate: z.string().nullish(),
});

export type NfcCardOrder = z.infer<typeof zNfcCardOrder>;

const zNfcCardOrdersPage = z.object({
	total: z.number(),
	elements: z.array(zNfcCardOrder),
});

export type NfcCardOrdersPage = PaginateSource<NfcCardOrder> & {
	/** Whether the orders could not be read, which is not the same as a card with none. */
	failed: boolean;
};

/**
 * One page of the orders loaded on a card, newest first.
 *
 * @param cardId The card id
 * @param page The page wanted, from 0
 * @param rowsPerPage How many orders a page holds
 */
export async function fetchCardOrders(cardId: string, page: number, rowsPerPage: number): Promise<NfcCardOrdersPage> {
	if (!isNfcCardId(cardId)) return { elements: [], total: 0, failed: true };

	try {
		const response = await api.get(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/orders`, {
			params: { options: JSON.stringify({ limit: rowsPerPage, skip: page * rowsPerPage }) },
		});

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return { ...zNfcCardOrdersPage.parse(response.data), failed: false };
	} catch (error) {
		console.error(error);
		return { elements: [], total: 0, failed: true };
	}
}

/**
 * What is loaded on a card: the cart as a bar order carries it, with the price and title
 * the catalogue has, because the API prices it again and refuses a line that differs.
 */
export type NfcCardLoad = {
	section: string;
	products: NfcCardOrderProduct[];
	total: number;
	/**
	 * Names the load, so a retried request answers the orders it already wrote instead of
	 * loading the cards twice.
	 */
	batchKey: string;
};

/**
 * What a load wrote, card by card, and whether this request was the one that wrote it. A
 * load is one order per unit (MUTE-614): `orders` has all of them, `order` is the first.
 */
export type NfcCardLoadResult = { cardId: string; created: boolean; order: NfcCardOrder; orders: NfcCardOrder[] };

const zNfcCardLoadResult = z.object({
	cardId: z.string(),
	created: z.boolean(),
	order: zNfcCardOrder,
	orders: z.array(zNfcCardOrder),
});

const zNfcCardLoadResults = z.object({ results: z.array(zNfcCardLoadResult) });

/** What a card was loaded with, in pesos: the total of every order of its load. */
export function getLoadTotal(orders: NfcCardOrder[] | null | undefined): number | undefined {
	return orders?.length ? orders.reduce((sum, order) => sum + order.total, 0) : undefined;
}

/**
 * Loads products on one card.
 *
 * @throws The request error when the API refuses it (a price or a product that does not
 *   match the catalogue or the section, a blocked or expired card), so the caller can show
 *   the API's own message
 */
export async function assignCardProducts(cardId: string, payload: NfcCardLoad): Promise<NfcCardLoadResult[]> {
	const response = await api.post(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/products`, payload);

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	// The body is the first order of the load, with every order of it in `orders`
	const { orders } = z.object({ orders: z.array(zNfcCardOrder) }).parse(response.data);

	return [{ cardId, created: response.status === 201, order: zNfcCardOrder.parse(response.data), orders }];
}

/**
 * Loads the same products on many cards, as one order per unit on each card. All or nothing: when a
 * card cannot be loaded the API loads none, and answers why each one failed (see
 * `getCardFailures`).
 *
 * @throws The request error when the API refuses the batch
 */
export async function assignCardsProducts(cardIds: string[], payload: NfcCardLoad): Promise<NfcCardLoadResult[]> {
	const response = await api.post(`${API_DOMAIN}${NFC_URL}/batch/products`, { ...payload, cardIds });

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return zNfcCardLoadResults.parse(response.data).results;
}

const zNfcCardFailures = z.object({
	response: z.object({
		data: z.object({ data: z.object({ failures: z.record(z.string()) }) }),
	}),
});

/**
 * The cards a refused load or batch named, each with the reason, or null when the refusal
 * was not about the cards (a price or a product, for instance).
 *
 * @param error What a load or a batch threw
 */
export function getCardFailures(error: unknown): { index: number; reason: string }[] | null {
	const parsed = zNfcCardFailures.safeParse(error);

	if (!parsed.success) return null;

	return Object.entries(parsed.data.response.data.data.failures).map(([index, reason]) => ({ index: Number(index), reason }));
}

/**
 * What a new batch of cards is registered with: the same label, responsable and expiration
 * on every card, and optionally a balance and a products load on every card.
 */
export type NfcCardBatchTemplate = {
	label: string | null;
	responsable: string | null;
	expiresAt: string | null;
	balance: number | null;
	load: Omit<NfcCardLoad, 'batchKey'> | null;
};

/** What a batch wrote on a card, and whether this request was the one that wrote it. */
const zNfcCardBatchResult = z.object({
	created: z.boolean(),
	card: zNfcCard,
	balance: z.number().nullish(),
	order: zNfcCardOrder.nullish(),
	orders: z.array(zNfcCardOrder).nullish(),
});

export type NfcCardBatchResult = z.infer<typeof zNfcCardBatchResult>;

const zNfcCardBatchResults = z.object({ results: z.array(zNfcCardBatchResult) });

/**
 * Registers a batch of cards with its template, in a single request.
 *
 * All or nothing: the API writes every card, its balance and its products in one
 * transaction, or none. A card that already exists refuses the whole batch, naming it (see
 * `getCardFailures`). Retrying the same `batchKey` with the same cards answers what it
 * already wrote, with `created` false, instead of writing it twice.
 *
 * @param uids The UIDs read, which the API registers by their keyed hash
 * @param template What every card is registered with
 * @param batchKey The key of the batch, drawn once when it is opened
 *
 * @throws The request error when the API refuses the batch
 */
export async function createNfcCardsBatch(
	uids: string[],
	template: NfcCardBatchTemplate,
	batchKey: string
): Promise<NfcCardBatchResult[]> {
	const response = await api.post(`${API_DOMAIN}${NFC_URL}/batch`, {
		cards: uids.map((uid) => ({ uid })),
		label: template.label,
		responsable: template.responsable,
		expiresAt: template.expiresAt,
		...(template.balance !== null ? { balance: template.balance } : {}),
		...(template.load ? template.load : {}),
		batchKey,
	});

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return zNfcCardBatchResults.parse(response.data).results;
}

/**
 * Cancels an order loaded on a card. Only one still waiting to be picked up can be
 * cancelled: the API answers a 409 otherwise, which is thrown as is.
 *
 * @param cardId The card id
 * @param orderId The order id
 */
export async function cancelCardOrder(cardId: string, orderId: string): Promise<NfcCardOrder> {
	const response = await api.delete(
		`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/orders/${encodeURIComponent(orderId)}`
	);

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return zNfcCardOrder.parse(response.data);
}

/**
 * The balance of a card, as `GET /nfc-card/:cardId/balance` answers it. It is the user balance
 * of the shadow user, read by the card id, so the shadow user is never part of the answer.
 *
 * `usable` is the API's own verdict on whether the card can be charged and loaded right now
 * (a blocked or expired card cannot), which the panel follows instead of deciding it again.
 */
export const zNfcCardBalance = z.object({
	_id: z.string(),
	label: z.string().nullish(),
	state: zNfcCardState,
	expiresAt: z.string().nullish(),
	usable: z.boolean(),
	balance: z.number(),
});

export type NfcCardBalance = z.infer<typeof zNfcCardBalance>;

const zNfcCardBalanceReset = zNfcCardBalance.extend({
	/** What the reset took off the card: its balance right before it. */
	resetAmount: z.number(),
});

export type NfcCardBalanceReset = z.infer<typeof zNfcCardBalanceReset>;

/**
 * The balance of a card, or null when it could not be read.
 *
 * @param cardId The card id
 */
export async function fetchCardBalance(cardId: string): Promise<NfcCardBalance | null> {
	if (!isNfcCardId(cardId)) return null;

	try {
		const response = await api.get(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/balance`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return zNfcCardBalance.parse(response.data);
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * Loads balance on a card.
 *
 * Idempotent by `operationId`: the API loads a key once, and a request that repeats it
 * answers the balance with a 200 instead of a 201 and adds nothing. The caller draws the key
 * once per load and sends the same one again on a retry; `created` tells it whether this
 * request was the one that loaded.
 *
 * @param cardId The card id
 * @param amount How much to load, in pesos
 * @param operationId The key of this load
 *
 * @throws The request error when the API refuses it (a blocked or expired card, the same key
 *   with another amount), so the caller can show the API's own message
 */
export async function addCardBalance(
	cardId: string,
	amount: number,
	operationId: string
): Promise<{ balance: NfcCardBalance; created: boolean }> {
	const response = await api.post(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/balance`, {
		amount,
		operationId,
	});

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return { balance: zNfcCardBalance.parse(response.data), created: response.status === 201 };
}

/**
 * Leaves the balance of a card at zero. It works on a blocked or expired card too.
 *
 * @param cardId The card id
 *
 * @throws The request error when the API refuses it
 */
export async function resetCardBalance(cardId: string): Promise<NfcCardBalanceReset> {
	const response = await api.delete(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/balance`);

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return zNfcCardBalanceReset.parse(response.data);
}

/**
 * Whether a failed request was answered and refused by the API, which means it changed
 * nothing. A failure without an answer (the network, a timeout) or with a 5xx may have
 * reached the balance anyway, and is retried with the same key rather than with a new one.
 *
 * @param error What the request threw
 */
export function isRefusedByApi(error: unknown): boolean {
	const status = isAxiosError(error) ? error.response?.status : undefined;
	return status !== undefined && status >= 400 && status < 500;
}


/**
 * The movements of the balance of a card, as the `status` of its ledger rows names them:
 * a load from the panel, a reset to zero, what the bar charged and what an order refunded.
 */
export const nfcCardTransactionTypes = ['approved', 'reset', 'order', 'order_refund_approved'] as const;

export type NfcCardTransactionType = (typeof nfcCardTransactionTypes)[number];

export function isNfcCardTransactionType(status: string): status is NfcCardTransactionType {
	return (nfcCardTransactionTypes as readonly string[]).includes(status);
}

/**
 * A ledger row of a card, as `GET /nfc-card/:cardId/transactions` answers it: the raw
 * `userBalanceTransactions` document. The schema keeps what the panel shows and strips the
 * rest, `username` (the shadow user) first of all.
 *
 * The status stays a string: a movement the panel does not name is still shown, as the API
 * wrote it, rather than failing the parse of its page.
 *
 * `balanceAfter` is the balance the API left the card with after the movement. Rows written
 * before it was stored do not carry it, and the panel does not work it out for them.
 */
export const zNfcCardTransaction = z.object({
	_id: z.string(),
	status: z.string(),
	amount: z.number(),
	operationId: z.string(),
	balanceAfter: z.number().nullish(),
	date: z.string().nullish(),
	createdAt: z.string().nullish(),
	addedBy: z.string().nullish(),
	resetBy: z.string().nullish(),
	cancelledBy: z.string().nullish(),
});

export type NfcCardTransaction = z.infer<typeof zNfcCardTransaction>;

const zNfcCardTransactionsEnvelope = z.object({
	data: z.array(zNfcCardTransaction),
	pagination: zWalletOperationPagination,
});

/** The largest page of movements the API answers. */
export const NFC_TRANSACTIONS_MAX_LIMIT = 100;

/**
 * What the movements of a card are narrowed by: the kind of movement, and the instants that
 * bound when it happened (the start of the first day and the end of the last, here). Both
 * ends are inclusive.
 */
export type NfcCardTransactionCriteria = {
	type?: NfcCardTransactionType;
	from?: string;
	to?: string;
};

export const emptyNfcCardTransactionCriteria: NfcCardTransactionCriteria = {};

export type NfcCardTransactionsPage = PaginateSource<NfcCardTransaction> & {
	/** Whether the movements could not be read, which is not the same as a card with none. */
	failed: boolean;
};

/**
 * One page of the movements of a card, newest first, narrowed and paged by the API.
 *
 * The filters are sent rather than applied here, so the total and the pages are those of
 * the movements asked for. The API refuses a type or a date it cannot read with a 400, which
 * is reported as a failed read.
 *
 * @param cardId The card id
 * @param criteria What to narrow to
 * @param page The page wanted, from 0
 * @param rowsPerPage How many movements a page holds, up to `NFC_TRANSACTIONS_MAX_LIMIT`
 */
export async function fetchCardTransactions(
	cardId: string,
	criteria: NfcCardTransactionCriteria,
	page: number,
	rowsPerPage: number
): Promise<NfcCardTransactionsPage> {
	if (!isNfcCardId(cardId)) return { elements: [], total: 0, failed: true };

	const limit = Math.min(rowsPerPage, NFC_TRANSACTIONS_MAX_LIMIT);

	try {
		const response = await api.get(`${API_DOMAIN}${NFC_URL}/${encodeURIComponent(cardId)}/transactions`, {
			params: {
				...(criteria.type ? { status: criteria.type } : {}),
				...(criteria.from ? { from: criteria.from } : {}),
				...(criteria.to ? { to: criteria.to } : {}),
				limit,
				skip: page * limit,
			},
		});

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const { data, pagination } = zNfcCardTransactionsEnvelope.parse(response.data);

		return { elements: data, total: pagination.total ?? data.length, failed: false };
	} catch (error) {
		console.error(error);
		return { elements: [], total: 0, failed: true };
	}
}

/**
 * Who made a movement: the admin who loaded or reset it, or who cancelled the order it
 * refunds. A charge at the bar has nobody recorded on the row.
 *
 * The card's own shadow user is never answered, even if a row ever names it: it means
 * nothing to an operator.
 *
 * @param transaction The movement
 * @param cardUsername The shadow username of the card, when it is known
 */
export function getTransactionActor(
	transaction: NfcCardTransaction,
	cardUsername?: string | null
): string | null {
	const actor =
		transaction.status === 'approved'
			? transaction.addedBy
			: transaction.status === 'reset'
				? transaction.resetBy
				: transaction.status === 'order_refund_approved'
					? transaction.cancelledBy
					: null;

	if (!actor || (!!cardUsername && actor.toLowerCase() === cardUsername.toLowerCase())) return null;

	return actor;
}

/**
 * The order a movement belongs to, for a charge or a refund. Both are keyed on the order id;
 * a load is keyed on the panel's own key, which is not an order.
 *
 * @param transaction The movement
 */
export function getTransactionOrderId(transaction: NfcCardTransaction): string | null {
	return transaction.status === 'order' || transaction.status === 'order_refund_approved'
		? transaction.operationId
		: null;
}

/**
 * Which way a movement moved the balance, to sign its amount on screen: a load and a refund
 * add, a charge and a reset (whose row holds what it took off) take off. Null for a movement
 * the panel does not name.
 *
 * @param transaction The movement
 */
export function getTransactionSign(transaction: NfcCardTransaction): 1 | -1 | null {
	switch (transaction.status) {
		case 'approved':
		case 'order_refund_approved':
			return 1;
		case 'order':
		case 'reset':
			return -1;
		default:
			return null;
	}
}

/** When a movement happened. */
export function getTransactionDate(transaction: NfcCardTransaction): string | null {
	return transaction.date ?? transaction.createdAt ?? null;
}
